import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import './Dashboard.css';
import './SettingsDash.css';
import { AuthContext } from '../../context/AuthContext';
import { FaEye, FaEyeSlash, FaPen } from 'react-icons/fa';
import { APP_CONFIG } from "../../config/appConfig";

const BASE_URL = APP_CONFIG.API_BASE_URL;

const maskEmail = (email) => {
  if (!email) return '';
  const [user, domain] = email.split('@');
  if (!user || !domain) return email;
  return user.length <= 2
    ? `${user}****@${domain}`
    : `${user.slice(0, 2)}****@${domain}`;
};

const PasswordResetModal = ({ onClose }) => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async () => {
    if (!code || !newPassword) return;
    setStatus('sending');

    try {
      const res = await fetch(`${BASE_URL}/auth/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, newPassword }),
      });

      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setTimeout(onClose, 2000);
      } else {
        setStatus(data.error || 'Error');
      }
    } catch (err) {
      console.error(err);
      setStatus('Error');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Reset Password</h2>
        <input
          type="text"
          placeholder="Enter code"
          value={code}
          onChange={e => setCode(e.target.value)}
        />
        <input
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
        />
        <button onClick={handleSubmit} disabled={status === 'sending'}>
          {status === 'sending' ? 'Saving...' : 'Reset Password'}
        </button>
        {status && status !== 'idle' && status !== 'sending' && <p>{status}</p>}
        <div className="modal-close" onClick={onClose}>Cancel</div>
      </div>
    </div>
  );
};

const SettingsDash = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, updateUser } = useContext(AuthContext);

  const [showEmail, setShowEmail] = useState(false);
  const [resetStatus, setResetStatus] = useState('idle');
  const [showResetModal, setShowResetModal] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameSaveStatus, setNameSaveStatus] = useState('idle');

  useEffect(() => {
    // Wait until auth state is resolved
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setNameSaveStatus('saving');

    try {
      const res = await fetch(`${BASE_URL}/auth/update-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update name');

      updateUser({ name: data.name });
      setEditingName(false);
      setNameSaveStatus('idle');
    } catch (err) {
      console.error(err);
      setNameSaveStatus('error');
    }
  };

  const handlePasswordReset = async () => {
    if (resetStatus !== 'idle') return;
    setResetStatus('sending');

    try {
      const res = await fetch(`${BASE_URL}/auth/request-password-reset`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) throw new Error('Reset failed');

      setResetStatus('sent');
      setShowResetModal(true);
    } catch {
      setResetStatus('error');
      setTimeout(() => setResetStatus('idle'), 3500);
    }
  };

  const capitalizeFirstLetter = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';


  if (authLoading) {
    return (
      <DashboardLayout>
          <div className="loading-spinner" />
      </DashboardLayout>
    );
  }

  if (!user) {
    return (
      <DashboardLayout>
          <div className="no-plan-message">
            <h1>Please sign in</h1>
            <button className="download-button" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
        <h1>Profile</h1>

        {/* Name */}
        <div className="settings-row settings-row-main">
          <span>Name</span>
          {editingName ? (
            <div className="settings-name-edit-group">
              <input
                type="text"
                className="settings-name-input"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                maxLength={100}
                autoFocus
              />
              <button
                className="settings-reset-btn"
                onClick={handleSaveName}
                disabled={nameSaveStatus === 'saving' || !nameInput.trim()}
              >
                {nameSaveStatus === 'saving' ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            <span
              className="settings-name-display"
              onClick={() => { setEditingName(true); setNameInput(user.name || ''); }}
            >
              {user.name || 'Add your name'} <FaPen size={13} className="settings-name-edit-icon" />
            </span>
          )}
        </div>
        {nameSaveStatus === 'error' && (
          <div className="settings-row settings-row-sub">
            <span style={{ color: '#ef4444' }}>Failed to update name. Please try again.</span>
          </div>
        )}
        <div className="settings-divider" />

        {/* Email */}
        <div className="settings-row settings-row-main">
          <span>Email</span>
          <span>{showEmail ? user.email : maskEmail(user.email)}</span>
        </div>
        <div className="settings-row settings-row-sub">
          <span>Where invoices and updates will be sent</span>
          <span onClick={() => setShowEmail(v => !v)} className="settings-show-email">
            Show email {showEmail ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        <div className="settings-divider" />

        {/* Password */}
        <div className="settings-row settings-row-main">
          <span>Password</span>
          <button
            className="settings-reset-btn"
            onClick={handlePasswordReset}
            disabled={resetStatus !== 'idle'}
          >
            {resetStatus === 'sending'
              ? 'Sending...'
              : resetStatus === 'sent'
              ? 'Check Email'
              : 'Reset'}
          </button>
        </div>
        <div className="settings-divider" />

        {/* Subscription - Now using user from AuthContext directly */}
        <div className="settings-subscription-container">
          <div className="settings-subscription-row">
            <span>Plan</span>
            <span>{capitalizeFirstLetter(user.planId || '—')}</span>
          </div>
          <div className="settings-subscription-row">
            <span>Blockchains</span>
            <span>
              {user.blockchains?.length
                ? user.blockchains.map(capitalizeFirstLetter).join(', ')
                : '—'}
            </span>
          </div>
        </div>

        <div className="settings-divider" />

        {/* Logout row */}
        <div className="settings-row settings-row-main">
          <span>Log Out</span>
          <button
            className="settings-logout-btn"
            onClick={async () => {
              await logout();
              navigate('/');
            }}
          >
            Log Out
          </button>
        </div>

        {/* Password Reset Modal */}
        {showResetModal && <PasswordResetModal onClose={() => setShowResetModal(false)} />}
    </DashboardLayout>
  );
};

export default SettingsDash;